import { useQuery } from '@tanstack/react-query';

import { IwQueryClient } from '../../store/IwQueryClient';

const MAIL_PREFILL_QUERY_KEY = 'getMailPrefill';

const ACQUISITION_CUSTOMER_API_BASE_URL =
  'https://acquisition-customer-api.iwoca.co.uk';

export type TMailPrefillResponse = {
  company_number: string;
};

type TMailPrefillError = Error & { statusCode: number };

async function fetchGetMailPrefill(
  token: string,
): Promise<TMailPrefillResponse> {
  const response = await fetch(
    `${ACQUISITION_CUSTOMER_API_BASE_URL}/api/mail/prefill?token=${encodeURIComponent(
      token,
    )}`,
  );

  if (!response.ok) {
    const error = new Error('Mail prefill request failed') as TMailPrefillError;
    error.statusCode = response.status;
    throw error;
  }

  const { company_number } = await response.json();
  return { company_number };
}

export const useGetMailPrefillQuery = (token?: string) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [MAIL_PREFILL_QUERY_KEY, token],
    queryFn: () => fetchGetMailPrefill(token as string),
    enabled: Boolean(token),
    gcTime: Infinity,
    retry: (failureCount, error) => {
      const { statusCode } = error as TMailPrefillError;
      return (
        failureCount < 1 && (statusCode === undefined || statusCode >= 500)
      );
    },
  });

  return {
    mailPrefill: data,
    isPendingMailPrefill: isPending,
    isMailPrefillError: isError,
    mailPrefillErrorStatus: (error as TMailPrefillError | null)?.statusCode,
  };
};

export const isCompanyFromMailPrefill = (
  companyNumber?: string | null,
): boolean => {
  if (!companyNumber) return false;

  const entries =
    IwQueryClient.getOrCreate().getQueriesData<TMailPrefillResponse>({
      queryKey: [MAIL_PREFILL_QUERY_KEY],
    });

  return entries.some(([, data]) => data?.company_number === companyNumber);
};
